import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Music, Save, Loader2, ExternalLink, Sparkles, Play } from "lucide-react";

/* /dashboard/jukebox-admin (admin-only)
   Mike's 20-row editor for /jukebox. Each row: Title + Artist + Amazon URL.
   Wires to GET /api/jukebox/songs and PUT /api/admin/jukebox/songs.
   The Amazon affiliate tag is appended on the BACKEND on every read, so
   Mike just pastes the raw Amazon Music URL and the system handles the rest.
*/

const G = "#2E9F3A";
const O = "#F39A1F";
const WINE = "#0F172A";

export default function JukeboxAdminTab() {
  const [songs, setSongs] = useState(() => Array.from({ length: 20 }, () => ({ title: "", artist: "", amazon_url: "", cover_url: "", youtube_id: "" })));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/jukebox/songs")
      .then((r) => {
        const loaded = r.data?.songs || [];
        const cleaned = loaded.map((s) => ({
          title: s.title || "",
          artist: s.artist || "",
          amazon_url: (s.amazon_url || "").replace(/[?&]tag=[^&]+$/, "").replace(/[?&]tag=[^&]+&/, "?"),
          cover_url: s.cover_url || "",
          youtube_id: s.youtube_id || "",
        }));
        const padded = Array.from({ length: 20 }, (_, i) => cleaned[i] || { title: "", artist: "", amazon_url: "", cover_url: "", youtube_id: "" });
        setSongs(padded);
        setDirty(false);
      })
      .catch(() => toast.error("Couldn't load the playlist."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const seedTop20 = async () => {
    if (!window.confirm("This will OVERWRITE every song slot with Mike's curated Top 20. Continue?")) return;
    setSeeding(true);
    try {
      await api.post("/admin/jukebox/seed");
      toast.success("Top 20 Christian songs seeded!");
      load();
    } catch (e) {
      toast.error("Couldn't seed. Try again.");
    } finally {
      setSeeding(false);
    }
  };

  const updateSong = (i, field, value) => {
    setSongs((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/jukebox/songs", { songs });
      toast.success("Playlist saved!");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="jukebox-admin-tab">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: O }}>
          <Music className="w-6 h-6" style={{ color: "#0F172A" }} />
        </div>
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: O }}>
            ★ Mike's Top 20 Christian Songs · Editor
          </div>
          <h2 className="font-serif-display text-3xl text-red-wine leading-tight">
            Your jukebox playlist
          </h2>
          <p className="text-sm text-foreground/75 mt-1">
            Paste a song <strong>title</strong>, <strong>artist</strong>, and the Amazon Music link.
            The system automatically appends your Amazon Associates tag to every link — so you earn commission on every play and purchase.
          </p>
          <a href="/jukebox" target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bold mt-2"
             style={{ color: G }}>
            Open Public Page <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button onClick={save} disabled={!dirty || saving}
                  data-testid="jukebox-save-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-xs font-bold uppercase tracking-[0.18em] shadow disabled:opacity-40 transition"
                  style={{ background: G, color: "white" }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving…" : dirty ? "Save Playlist" : "Saved"}
          </button>
          <button onClick={seedTop20} disabled={seeding}
                  data-testid="jukebox-seed-btn"
                  title="Wipe + replace with 20 popular Christian songs (YouTube + Amazon)"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-xs font-bold uppercase tracking-[0.18em] transition hover:scale-[1.02] whitespace-nowrap"
                  style={{ background: "#FFFFFF", color: WINE, border: `1.5px solid ${WINE}` }}>
            {seeding ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
            {seeding ? "Seeding…" : "Seed Top 20"}
          </button>
        </div>
      </div>

      <div className="rounded-xl border-2 border-border bg-white overflow-hidden shadow-md">
        {loading ? (
          <div className="p-10 text-center text-foreground/60">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading playlist…
          </div>
        ) : (
          <div className="divide-y divide-border">
            {/* Header row — labels so Mike never has to guess */}
            <div className="grid grid-cols-[40px_56px_1fr_1fr_1.4fr_120px_120px] gap-2 items-center p-3 sticky top-0 bg-white z-10"
                 style={{ borderBottom: `2px solid ${O}55` }}>
              <div className="text-[9px] tracking-[0.22em] uppercase font-bold text-center" style={{ color: WINE }}>#</div>
              <div className="text-[9px] tracking-[0.22em] uppercase font-bold text-center" style={{ color: WINE }}>Cover</div>
              <div className="text-[9px] tracking-[0.22em] uppercase font-bold" style={{ color: WINE }}>Song Title</div>
              <div className="text-[9px] tracking-[0.22em] uppercase font-bold" style={{ color: WINE }}>Artist</div>
              <div className="text-[9px] tracking-[0.22em] uppercase font-bold" style={{ color: WINE }}>Amazon URL (Buy)</div>
              <div className="text-[9px] tracking-[0.22em] uppercase font-bold flex items-center gap-1" style={{ color: G }}>
                <Play className="w-3 h-3"/> YouTube ID
              </div>
              <div className="text-[9px] tracking-[0.22em] uppercase font-bold" style={{ color: WINE }}>Cover URL</div>
            </div>
            {songs.map((s, i) => (
              <div key={i} className="grid grid-cols-[40px_56px_1fr_1fr_1.4fr_120px_120px] gap-2 items-center p-3" data-testid={`jukebox-row-${i + 1}`}>
                <div className="font-serif-display text-2xl text-center font-bold" style={{ color: WINE }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                {/* Cover-art preview thumbnail */}
                <div className="w-12 h-12 rounded-md overflow-hidden flex items-center justify-center"
                     style={{ background: s.cover_url ? "transparent" : "#FFFFFF", border: `2px solid ${O}55` }}>
                  {s.cover_url ? (
                    <img src={s.cover_url} alt="cover" className="block w-full h-full object-cover"
                         onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <Music className="w-5 h-5" style={{ color: O }} />
                  )}
                </div>
                <input
                  value={s.title}
                  onChange={(e) => updateSong(i, "title", e.target.value)}
                  placeholder="Song title"
                  data-testid={`jukebox-title-${i + 1}`}
                  className="px-3 py-2 rounded border-2 border-border bg-white text-sm focus:border-[#F39A1F] outline-none"
                />
                <input
                  value={s.artist}
                  onChange={(e) => updateSong(i, "artist", e.target.value)}
                  placeholder="Artist"
                  data-testid={`jukebox-artist-${i + 1}`}
                  className="px-3 py-2 rounded border-2 border-border bg-white text-sm focus:border-[#F39A1F] outline-none"
                />
                <input
                  value={s.amazon_url}
                  onChange={(e) => updateSong(i, "amazon_url", e.target.value)}
                  placeholder="https://amazon.com/..."
                  data-testid={`jukebox-url-${i + 1}`}
                  className="px-3 py-2 rounded border-2 border-border bg-white text-xs font-mono focus:border-[#F39A1F] outline-none"
                />
                <input
                  value={s.youtube_id}
                  onChange={(e) => {
                    // Accept full YouTube URLs OR raw IDs — auto-extract the 11-char ID
                    let v = e.target.value.trim();
                    const m = v.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
                    if (m) v = m[1];
                    updateSong(i, "youtube_id", v);
                  }}
                  placeholder="dQw4w9WgXcQ"
                  data-testid={`jukebox-youtube-${i + 1}`}
                  className="px-3 py-2 rounded border-2 bg-white text-xs font-mono focus:border-[#2E9F3A] outline-none"
                  style={{ borderColor: s.youtube_id ? `${G}88` : "var(--border)" }}
                />
                <input
                  value={s.cover_url}
                  onChange={(e) => updateSong(i, "cover_url", e.target.value)}
                  placeholder="cover.jpg"
                  data-testid={`jukebox-cover-${i + 1}`}
                  className="px-3 py-2 rounded border-2 border-border bg-white text-xs font-mono focus:border-[#F39A1F] outline-none"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg p-4" style={{ background: "rgba(243,154,31,0.08)", border: `1px solid ${O}55` }}>
        <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: WINE }}>
          ★ Setup tip · Amazon Associates Tag
        </div>
        <p className="text-xs text-foreground/80">
          Your associates tag is currently set to <code className="bg-white px-1.5 py-0.5 rounded text-[11px] font-mono">{process.env.REACT_APP_AMAZON_TAG || "faitharama-20"}</code>.
          Once you have your real Amazon Associates ID, send it to Mike-the-builder and he'll swap the env var — no code changes needed, takes 30 seconds.
        </p>
      </div>
    </div>
  );
}
